// src/context/SceneProvider.tsx
import { createContext, useCallback, useState, useEffect, ReactNode, FC } from 'react';
import { useParams } from 'react-router-dom';
import { SupabaseSceneInstance, SupabaseSceneOrigin } from '@/types/sceneTypes'; // Adjust the import path as necessary
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { fetchSceneRow, fetchStoryRow } from '@/modules/databaseService/scenes';
import { sceneOpening } from '@/infrastructure/api/v1/client';
import { latestAnchorCategory } from '@/hooks/useSceneAnchorCategory';

import { onEditorChange } from '@/modules/undoRedo';
export type SceneAnchorCategory = 'place_anywhere' | 'immersal' | 'image_anchor' | 'geospatial_anchor';

export interface SceneContextType {
  sceneInstance: SupabaseSceneInstance;
  sceneOrigin: SupabaseSceneOrigin | null;
  error: string | null;
  loading: boolean;
  refreshScene: () => Promise<void>;
  anchorCategory: SceneAnchorCategory | null;
  refreshAnchorCategory: () => Promise<void>;
  publishFlowOpen: boolean;
  setPublishFlowOpen: (open: boolean) => void;
}

const SceneContext = createContext<SceneContextType | undefined>(undefined);

const defaultSceneInstance: SupabaseSceneInstance = {
  user_id: '',
  scene_instance_id: '',
  project_instance_id: '',
  scene_origin_id: '',
};

interface SceneProviderProps {
  children: ReactNode;
}

export const SceneProvider: FC<SceneProviderProps> = ({ children }) => {
  const { sceneId } = useParams<{ sceneId: string }>();
  const services = useServices();
  const [sceneInstance, setSceneInstance] =
    useState<SupabaseSceneInstance>(defaultSceneInstance);
  const [sceneOrigin, setSceneOrigin] = useState<SupabaseSceneOrigin | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [anchorCategory, setAnchorCategory] = useState<SceneAnchorCategory | null>(null);
  const [publishFlowOpen, setPublishFlowOpen] = useState(false);

  const fetchAnchorCategory = useCallback(async (id: string) => {
    setAnchorCategory(await latestAnchorCategory(id));
  }, []);

  const refreshAnchorCategory = useCallback(async () => {
    const id = sceneInstance.scene_instance_id;
    if (!id) {
      setAnchorCategory(null);
      return;
    }
    try {
      await fetchAnchorCategory(id);
    } catch (err) {
      console.error('refreshAnchorCategory failed:', err);
    }
  }, [sceneInstance.scene_instance_id, fetchAnchorCategory]);

  const fetchSceneInstance = useCallback(async () => {
    // Without a scene ID there is nothing to load; render reports it below
    if (!sceneId) return;

    await fetchSceneRow(sceneId)
      .then(async ({ data, error }) => {
        if (error) {
          throw error;
        }

        setSceneInstance(data as unknown as SupabaseSceneInstance);

        // Fetch scene origin data
        if (data.scene_origin_id) {
          const { data: originData, error: originError } = await fetchStoryRow(data.scene_origin_id);

          if (originError) {
            console.error('Error fetching scene origin:', originError);
            setSceneOrigin(null);
          } else {
            setSceneOrigin(originData as unknown as SupabaseSceneOrigin);
          }
        }
      })
      .catch((error) => {
        setError((error as Error).message);
        console.error('Error fetching scene instance:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [sceneId]);

  const refreshScene = useCallback(async () => {
    setLoading(true);
    await fetchSceneInstance();
  }, [fetchSceneInstance]);

  // What the editor reads as it opens the scene (the scene, its story and project, its rows, materials, health, library) is one read of its workspace
  useEffect(() => {
    if (sceneId) sceneOpening(sceneId);
  }, [sceneId]);

  useEffect(() => {
    fetchSceneInstance();
  }, [fetchSceneInstance]);

  // Anchor add/delete/swap happens outside this provider (library utils, upload
  // wizard, swap service); they fire `sceneAnchorChanged` so the cached scene row
  // and derived anchor category re-sync without a full page reload (lureo-docs#259).
  useEffect(() => {
    const onAnchorChanged = () => {
      void refreshScene();
      void refreshAnchorCategory();
    };
    window.addEventListener('sceneAnchorChanged', onAnchorChanged);
    return () => window.removeEventListener('sceneAnchorChanged', onAnchorChanged);
  }, [refreshScene, refreshAnchorCategory]);

  // Undo and redo rewrite the scene's settings and its story's name underneath the provider
  useEffect(() => onEditorChange(change => {
    if (change.kind === 'scene' || change.kind === 'story') void refreshScene();
  }), [refreshScene]);

  // Losing the scene instance clears its anchor category
  const sceneInstanceId = sceneInstance.scene_instance_id;
  const [anchorSceneId, setAnchorSceneId] = useState(sceneInstanceId);
  if (anchorSceneId !== sceneInstanceId) {
    setAnchorSceneId(sceneInstanceId);
    if (!sceneInstanceId) setAnchorCategory(null);
  }

  useEffect(() => {
    const id = sceneInstance.scene_instance_id;
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        await fetchAnchorCategory(id);
      } catch (err) {
        if (!cancelled) console.error('Anchor category fetch failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sceneInstance.scene_instance_id, services.scene, fetchAnchorCategory]);

  const sceneError = sceneId ? error : 'No scene ID provided';
  const sceneLoading = sceneId ? loading : false;

  return (
    <SceneContext.Provider value={{ sceneInstance, sceneOrigin, error: sceneError, loading: sceneLoading, refreshScene, anchorCategory, refreshAnchorCategory, publishFlowOpen, setPublishFlowOpen }}>
      {children}
    </SceneContext.Provider>
  );
};

export { SceneContext };
